import { useEffect, useRef, useState } from "react";
import { useForm, UseFormRegister } from "react-hook-form";
import { FileText, Pencil, Plus, Trash2, Upload } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import { downloadProtectedFile } from "../../api/files";
import * as catalogoApi from "../../api/catalogo";
import { DispositivoCatalogo, DispositivoDocumento, DocumentoTipo } from "../../api/catalogo";
import { TipoDispositivo } from "../../types";
import { ACCEPT_CATALOGO } from "../../utils/tipiFileCaricati";
import { Table, TableColumn } from "../../components/ui/Table";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { SlideOver } from "../../components/ui/SlideOver";

const TIPI_DISPOSITIVO: TipoDispositivo[] = ["Cassaforte", "Rendiresto", "TVCC", "Allarme", "Altro"];

const DOCUMENTO_LABELS: Record<DocumentoTipo, string> = {
  scheda_tecnica: "Scheda tecnica",
  disegno: "Disegno",
  foto: "Foto",
  manuale: "Manuale",
  video: "Video",
};
const DOCUMENTO_TIPI: DocumentoTipo[] = ["scheda_tecnica", "disegno", "foto", "manuale", "video"];

// I sei campi misura (cm), in ordine, con etichetta di gruppo/dimensione.
const MISURA_CAMPI = [
  { key: "misure_cassaforte_lunghezza", gruppo: "cassaforte", dim: "Lunghezza" },
  { key: "misure_cassaforte_profondita", gruppo: "cassaforte", dim: "Profondità" },
  { key: "misure_cassaforte_altezza", gruppo: "cassaforte", dim: "Altezza" },
  { key: "misure_ingombro_lunghezza", gruppo: "ingombro", dim: "Lunghezza" },
  { key: "misure_ingombro_profondita", gruppo: "ingombro", dim: "Profondità" },
  { key: "misure_ingombro_altezza", gruppo: "ingombro", dim: "Altezza" },
] as const;

/** "60 × 55 × 90 cm" oppure null se manca almeno una delle tre misure. */
function formatTerna(l: string | null, p: string | null, h: string | null): string | null {
  if (!l || !p || !h) return null;
  const n = (v: string) => String(Number(v)); // "60.0" -> "60"
  return `${n(l)} × ${n(p)} × ${n(h)} cm`;
}

export default function Catalogo() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const canManage = user?.role === "admin" || user?.role === "staff";

  const [dispositivi, setDispositivi] = useState<DispositivoCatalogo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filtro, setFiltro] = useState<TipoDispositivo | "">("");
  const [selected, setSelected] = useState<DispositivoCatalogo | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  async function load() {
    setIsLoading(true);
    try {
      const { dispositivi: list } = await catalogoApi.listCatalogo(filtro || undefined);
      setDispositivi(list);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare il catalogo."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro]);

  function openDetail(d: DispositivoCatalogo) {
    setSelected(d);
    setPanelOpen(true);
  }

  async function refreshSelected() {
    if (!selected) return;
    const { dispositivi: list } = await catalogoApi.listCatalogo();
    setDispositivi(list);
    const updated = list.find((d) => d.id === selected.id);
    if (updated) setSelected(updated);
  }

  const columns: TableColumn<DispositivoCatalogo>[] = [
    {
      key: "tipo_dispositivo",
      header: "Tipo",
      sortValue: (d) => d.tipo_dispositivo,
      render: (d) => <Badge color="primary">{d.tipo_dispositivo}</Badge>,
    },
    // Marca e modello sono insieme l'identificativo della riga — sul database
    // sono la coppia @unique. Spegnerne una lascerebbe un elenco di righe che
    // non si distinguono fra loro, quindi non si spengono.
    { key: "marca", header: "Marca", sortValue: (d) => d.marca, sempreVisibile: true },
    { key: "modello", header: "Modello", sortValue: (d) => d.modello, sempreVisibile: true },
    { key: "sigla", header: "Sigla", sortValue: (d) => d.sigla, render: (d) => d.sigla ?? <span className="text-ink/30">—</span> },
    {
      key: "documenti",
      header: "Documentazione",
      // Si ordina sul numero di file, non sull'etichetta: serve a trovare i
      // modelli ancora senza documentazione, che con i vuoti in fondo stanno
      // tutti insieme in un punto solo.
      sortValue: (d) => d.documenti.length,
      render: (d) =>
        d.documenti.length > 0 ? (
          <span className="text-xs text-ink/60">{d.documenti.length} file</span>
        ) : (
          <span className="text-xs text-ink/30">Nessun documento</span>
        ),
    },
    // Le due terne di misure: in scheda ci sono già, qui servono a confrontare
    // due modelli senza aprirli uno per uno. Spente di default perché oggi in
    // anagrafica non è compilata nessuna delle due — il campo esiste dalla
    // Fase 4 ma non è ancora stato riempito.
    //
    // Senza `sortValue` di proposito: il valore mostrato è una terna, e
    // ordinarla come testo ("100 × ..." prima di "60 × ...") sarebbe sbagliato,
    // mentre ordinarla su un criterio non visibile — il volume, la lunghezza —
    // darebbe un ordine che dalla colonna non si spiega.
    {
      key: "misure_cassaforte",
      header: "Misure cassaforte",
      nascostaDiDefault: true,
      render: (d) => {
        const t = formatTerna(
          d.misure_cassaforte_lunghezza,
          d.misure_cassaforte_profondita,
          d.misure_cassaforte_altezza
        );
        return t ? <span className="text-xs tabular-nums text-ink/70">{t}</span> : <span className="text-ink/30">—</span>;
      },
    },
    {
      key: "misure_ingombro",
      header: "Misure ingombro",
      nascostaDiDefault: true,
      render: (d) => {
        const t = formatTerna(
          d.misure_ingombro_lunghezza,
          d.misure_ingombro_profondita,
          d.misure_ingombro_altezza
        );
        return t ? <span className="text-xs tabular-nums text-ink/70">{t}</span> : <span className="text-ink/30">—</span>;
      },
    },
  ];

  return (
    <div>
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink">Catalogo Dispositivi</h1>
          <p className="mt-1 text-sm text-ink/50">
            Schede tecniche, disegni, foto, video e manuali — consultabili da Installazioni, Sopralluoghi, Attivazioni e
            Interventi.
          </p>
        </div>
        {canManage && (
          <Button onClick={() => setNewOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nuovo dispositivo
          </Button>
        )}
      </header>

      <div className="mb-4 max-w-xs">
        <select
          value={filtro}
          onChange={(e) => setFiltro(e.target.value as TipoDispositivo | "")}
          className="h-10 w-full rounded-lg border border-line bg-white px-3 text-sm text-ink"
        >
          <option value="">Tutti i tipi</option>
          {TIPI_DISPOSITIVO.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <Table
        columns={columns}
        data={dispositivi}
        rowKey={(d) => d.id}
        isLoading={isLoading}
        emptyMessage="Nessun dispositivo nel catalogo."
        onRowClick={openDetail}
        ordinabile
        sceltaColonne="catalogo"
      />

      <SlideOver
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        title={selected ? `${selected.marca} ${selected.modello}` : "Dispositivo"}
        size="lg"
      >
        {selected && (
          <div className="flex flex-col gap-4">
            <div className="rounded-lg bg-primary/5 px-4 py-3 text-sm">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <Badge color="primary">{selected.tipo_dispositivo}</Badge>
                  {selected.sigla && <p className="mt-2 text-ink/70">Sigla: <span className="font-semibold text-ink">{selected.sigla}</span></p>}
                </div>
                {canManage && (
                  <Button type="button" size="sm" variant="secondary" onClick={() => setEditOpen(true)}>
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                    Modifica
                  </Button>
                )}
              </div>
              {selected.descrizione && <p className="mt-2 text-ink/70">{selected.descrizione}</p>}
            </div>

            {/* Misure numeriche (L × P × H cm), mostrate solo se valorizzate. */}
            {(() => {
              const cassaforte = formatTerna(
                selected.misure_cassaforte_lunghezza,
                selected.misure_cassaforte_profondita,
                selected.misure_cassaforte_altezza
              );
              const ingombro = formatTerna(
                selected.misure_ingombro_lunghezza,
                selected.misure_ingombro_profondita,
                selected.misure_ingombro_altezza
              );
              if (!cassaforte && !ingombro) return null;
              return (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg border border-line px-4 py-3">
                    <p className="text-xs font-bold uppercase tracking-wide text-ink/50">Misure cassaforte</p>
                    <p className="mt-1 font-semibold text-ink">{cassaforte ?? "—"}</p>
                  </div>
                  <div className="rounded-lg border border-line px-4 py-3">
                    <p className="text-xs font-bold uppercase tracking-wide text-ink/50">Misure ingombro</p>
                    <p className="mt-1 font-semibold text-ink">{ingombro ?? "—"}</p>
                  </div>
                </div>
              );
            })()}

            {DOCUMENTO_TIPI.map((tipo) => (
              <DocumentoSection
                key={tipo}
                dispositivoId={selected.id}
                tipo={tipo}
                documenti={selected.documenti.filter((d) => d.tipo === tipo)}
                canManage={canManage}
                onChange={refreshSelected}
              />
            ))}
          </div>
        )}
      </SlideOver>

      {canManage && <NuovoDispositivoModal open={newOpen} onClose={() => setNewOpen(false)} onCreated={load} />}
      {canManage && selected && (
        <ModificaDispositivoModal
          key={selected.id}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          dispositivo={selected}
          onSaved={refreshSelected}
        />
      )}
    </div>
  );
}

// ============================================================================
// Sezione di una singola tipologia di documento (Scheda tecnica, Disegno, ecc.)
// ============================================================================
function DocumentoSection({
  dispositivoId,
  tipo,
  documenti,
  canManage,
  onChange,
}: {
  dispositivoId: string;
  tipo: DocumentoTipo;
  documenti: DispositivoDocumento[];
  canManage: boolean;
  onChange: () => void;
}) {
  const { showToast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      await catalogoApi.uploadDocumento(dispositivoId, tipo, file);
      showToast("Documento caricato.", "success");
      onChange();
    } catch (err) {
      showToast(getErrorMessage(err, "Caricamento non riuscito."), "error");
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  }

  async function handleDelete(doc: DispositivoDocumento) {
    const confirmed = window.confirm(`Eliminare "${doc.nome_file}"?`);
    if (!confirmed) return;
    setDeletingId(doc.id);
    try {
      await catalogoApi.deleteDocumento(doc.id);
      showToast("Documento eliminato.", "success");
      onChange();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare il documento."), "error");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="rounded-lg border border-line bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-medium text-ink">{DOCUMENTO_LABELS[tipo]}</p>
        {canManage && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT_CATALOGO}
              onChange={handleFile}
              className="hidden"
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              loading={isUploading}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="h-3.5 w-3.5" aria-hidden="true" />
              Carica
            </Button>
          </>
        )}
      </div>

      {documenti.length === 0 ? (
        <p className="text-xs text-ink/40">Nessun file.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {documenti.map((doc) => (
            <li key={doc.id} className="flex items-center justify-between gap-2 rounded bg-surface px-2 py-1.5 text-xs">
              <button
                type="button"
                onClick={() =>
                  downloadProtectedFile(doc.file_url, doc.nome_file ?? "documento").catch((err) =>
                    showToast(getErrorMessage(err, "Impossibile scaricare il documento."), "error")
                  )
                }
                className="flex items-center gap-1.5 truncate text-primary hover:underline"
              >
                <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate">{doc.nome_file}</span>
              </button>
              {canManage && (
                <button
                  type="button"
                  onClick={() => handleDelete(doc)}
                  disabled={deletingId === doc.id}
                  aria-label="Elimina documento"
                  className="shrink-0 text-ink/40 hover:text-accent disabled:opacity-40"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ============================================================================
// Modal — nuovo dispositivo
// ============================================================================
type NuovoDispositivoForm = {
  tipo_dispositivo: string;
  marca: string;
  modello: string;
  sigla: string;
  descrizione: string;
  // Le sei misure come stringhe (input di form). Vuote = non impostate.
  misure_cassaforte_lunghezza: string;
  misure_cassaforte_profondita: string;
  misure_cassaforte_altezza: string;
  misure_ingombro_lunghezza: string;
  misure_ingombro_profondita: string;
  misure_ingombro_altezza: string;
};

/** Converte le sei misure del form in payload numerico (vuote -> null). */
function misureToPayload(values: NuovoDispositivoForm): catalogoApi.MisurePayload {
  const out: catalogoApi.MisurePayload = {};
  for (const { key } of MISURA_CAMPI) {
    const raw = (values[key] ?? "").trim();
    out[key] = raw ? Number(raw.replace(",", ".")) : null;
  }
  return out;
}

/** Valori di default delle sei misure per il form di modifica (null -> ""). */
function misureDefaults(d: DispositivoCatalogo): Pick<NuovoDispositivoForm, (typeof MISURA_CAMPI)[number]["key"]> {
  const out = {} as Record<string, string>;
  for (const { key } of MISURA_CAMPI) out[key] = d[key] != null ? String(Number(d[key])) : "";
  return out as Pick<NuovoDispositivoForm, (typeof MISURA_CAMPI)[number]["key"]>;
}

/** Due gruppi (cassaforte / ingombro) con tre input numerici L/P/H in cm. */
function MisureFieldset({ register }: { register: UseFormRegister<NuovoDispositivoForm> }) {
  const gruppi = [
    { nome: "cassaforte" as const, titolo: "Misure cassaforte" },
    { nome: "ingombro" as const, titolo: "Misure ingombro" },
  ];
  return (
    <>
      {gruppi.map((g) => (
        <div key={g.nome} className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">{g.titolo} (cm)</label>
          <div className="grid grid-cols-3 gap-2">
            {MISURA_CAMPI.filter((c) => c.gruppo === g.nome).map((c) => (
              <input
                key={c.key}
                type="number"
                step="0.1"
                min="1"
                max="500"
                placeholder={c.dim}
                aria-label={`${g.titolo} — ${c.dim}`}
                className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
                {...register(c.key)}
              />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

function NuovoDispositivoModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { showToast } = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<NuovoDispositivoForm>({ defaultValues: { tipo_dispositivo: "Cassaforte" } });

  async function onSubmit(values: NuovoDispositivoForm) {
    try {
      await catalogoApi.createDispositivo({
        tipo_dispositivo: values.tipo_dispositivo as TipoDispositivo,
        marca: values.marca,
        modello: values.modello,
        sigla: values.sigla || undefined,
        descrizione: values.descrizione || undefined,
        ...misureToPayload(values),
      });
      showToast("Dispositivo aggiunto al catalogo.", "success");
      reset();
      onClose();
      onCreated();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile aggiungere il dispositivo."), "error");
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nuovo dispositivo"
      size="sm"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" form="nuovo-dispositivo-form" loading={isSubmitting}>
            Crea
          </Button>
        </>
      }
    >
      <form id="nuovo-dispositivo-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">
            Tipo dispositivo <span className="text-accent">*</span>
          </label>
          <select
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            {...register("tipo_dispositivo", { required: true })}
          >
            {TIPI_DISPOSITIVO.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <Input
          label="Marca"
          required
          error={errors.marca?.message}
          {...register("marca", { required: "La marca è obbligatoria." })}
        />
        <Input
          label="Modello"
          required
          error={errors.modello?.message}
          {...register("modello", { required: "Il modello è obbligatorio." })}
        />
        <Input
          label="Sigla"
          hint="Es. per le casseforti, la sigla con cui sono identificate sul campo."
          {...register("sigla")}
        />

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Descrizione</label>
          <textarea
            rows={2}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
            {...register("descrizione")}
          />
        </div>

        <MisureFieldset register={register} />
      </form>
    </Modal>
  );
}

// ============================================================================
// Modal — modifica dispositivo esistente (tipo/marca/modello/sigla/descrizione)
// ============================================================================
function ModificaDispositivoModal({
  open,
  onClose,
  dispositivo,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  dispositivo: DispositivoCatalogo;
  onSaved: () => void;
}) {
  const { showToast } = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<NuovoDispositivoForm>({
    defaultValues: {
      tipo_dispositivo: dispositivo.tipo_dispositivo,
      marca: dispositivo.marca,
      modello: dispositivo.modello,
      sigla: dispositivo.sigla ?? "",
      descrizione: dispositivo.descrizione ?? "",
      ...misureDefaults(dispositivo),
    },
  });

  // Il form non si auto-aggiorna riaprendo su un dispositivo diverso perché
  // useForm calcola i defaultValues una sola volta al mount — qui invece la
  // modal viene rimontata ogni volta grazie alla key sull'id in Catalogo.tsx.

  async function onSubmit(values: NuovoDispositivoForm) {
    try {
      await catalogoApi.updateDispositivo(dispositivo.id, {
        tipo_dispositivo: values.tipo_dispositivo as TipoDispositivo,
        marca: values.marca,
        modello: values.modello,
        sigla: values.sigla,
        descrizione: values.descrizione,
        ...misureToPayload(values),
      });
      showToast("Dispositivo aggiornato.", "success");
      onClose();
      onSaved();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile aggiornare il dispositivo."), "error");
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Modifica dispositivo"
      size="sm"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit" form="modifica-dispositivo-form" loading={isSubmitting}>
            Salva
          </Button>
        </>
      }
    >
      <form id="modifica-dispositivo-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">
            Tipo dispositivo <span className="text-accent">*</span>
          </label>
          <select
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            {...register("tipo_dispositivo", { required: true })}
          >
            {TIPI_DISPOSITIVO.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <Input
          label="Marca"
          required
          error={errors.marca?.message}
          {...register("marca", { required: "La marca è obbligatoria." })}
        />
        <Input
          label="Modello"
          required
          error={errors.modello?.message}
          {...register("modello", { required: "Il modello è obbligatorio." })}
        />
        <Input label="Sigla" {...register("sigla")} />

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Descrizione</label>
          <textarea
            rows={2}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
            {...register("descrizione")}
          />
        </div>

        <MisureFieldset register={register} />
      </form>
    </Modal>
  );
}
